// MJOS-147 — pure message-conversion layer for the assistant-ui external store.
//
// Two pure, unit-testable functions sit between pi-agent-core's transcript and
// assistant-ui's renderer:
//
//   toDisplayMessages(messages, runningId?)
//     Folds each standalone pi `toolResult` message into the matching
//     `toolCall` part of the preceding assistant message (matched on
//     toolCallId), attaching its text + isError as the part's `result`, then
//     DROPS the standalone toolResult. The result is a transcript where every
//     tool call carries its own result — which is exactly the shape
//     assistant-ui renders inline on the assistant turn. It also stamps a
//     stable `id` on every message (the message's own id if pi provides one,
//     otherwise `committed-${index}`) and a `status`: the message whose id
//     matches `runningId` is `{type:"running"}`, everything else is
//     `{type:"complete"}`.
//
//   convertMessage(message)
//     Maps ONE enriched pi AgentMessage → assistant-ui ThreadMessageLike.
//     Stable module-level fn (so useExternalMessageConverter can memo-cache by
//     object identity). Defensive: never throws.
//
// Keeping this out of the React provider means the streaming/abort wiring in
// MyJarvisRuntimeProvider stays small, and the conversion is testable in
// isolation.
import type { ThreadMessageLike } from "@assistant-ui/react";
import type { AgentMessage } from "@earendil-works/pi-agent-core";

type ThreadMessagePart = Exclude<ThreadMessageLike["content"], string>[number];

/** An AgentMessage decorated with the display id + running/complete status. */
export type DisplayMessage = AgentMessage & {
	__displayId: string;
	__running: boolean;
};

/**
 * Optional identity-preserving cache for toDisplayMessages. assistant-ui's
 * useExternalMessageConverter memoizes converted output by INPUT-OBJECT
 * IDENTITY (WeakMap) — so a committed message that hasn't changed must map to
 * the SAME decorated object across renders, or every token re-converts the
 * whole transcript and remounts every bubble. The provider holds one cache for
 * the panel's lifetime and passes it on every call. The cache keys on the
 * SOURCE pi message object; an entry is reused only when the running flag and
 * the attached-result set are both unchanged.
 */
export type DisplayCache = WeakMap<object, CacheEntry>;
interface CacheEntry {
	decorated: DisplayMessage;
	running: boolean;
	resultKey: string; // serialized signature of attached tool results
}

export function createDisplayCache(): DisplayCache {
	return new WeakMap();
}

function textOf(content: unknown): string {
	if (typeof content === "string") return content;
	if (Array.isArray(content)) {
		return content
			.filter((c) => (c as { type?: string })?.type === "text")
			.map((c) => String((c as { text?: unknown }).text ?? ""))
			.join("\n")
			.trim();
	}
	return "";
}

/**
 * Transform the raw pi transcript into a display list:
 *   - fold every `toolResult` message into the matching `toolCall` part of the
 *     preceding assistant message (match on toolCallId), then drop it;
 *   - stamp a stable display id + running flag on each surviving message.
 *
 * `runningId` is the id of the in-flight streaming partial (if any) — only that
 * message is marked running.
 */
export function toDisplayMessages(
	messages: AgentMessage[],
	runningId?: string,
	cache?: DisplayCache,
): DisplayMessage[] {
	// --- Pass 1: figure out which assistant message owns each tool-call id, and
	// collect the toolResults that fold into it. We DON'T mutate the source — we
	// record, per source message, the results to attach + a signature so the
	// identity cache can detect when nothing changed.
	const ownerByToolCallId = new Map<string, object>();
	// source assistant message → (toolCallId → { result, isError })
	const resultsFor = new Map<
		object,
		Map<string, { result: unknown; isError: boolean }>
	>();

	for (const message of messages) {
		const raw = message as unknown as Record<string, unknown>;
		if (raw?.role === "toolResult") {
			const toolCallId = String(raw.toolCallId ?? "");
			const owner = toolCallId ? ownerByToolCallId.get(toolCallId) : undefined;
			if (owner) {
				const text = textOf(raw.content);
				let bag = resultsFor.get(owner);
				if (!bag) resultsFor.set(owner, (bag = new Map()));
				bag.set(toolCallId, {
					result: text || raw.content,
					isError: !!raw.isError,
				});
			}
			continue;
		}
		const content = raw?.content;
		if (Array.isArray(content)) {
			for (const p of content) {
				const pp = p as Record<string, unknown>;
				if (pp?.type === "toolCall") {
					const id = String(pp.id ?? "");
					if (id) ownerByToolCallId.set(id, message as object);
				}
			}
		}
	}

	// --- Pass 2: decorate every non-toolResult message, dropping toolResults.
	// Reuse a cached decorated object when source identity + running flag +
	// attached-result signature are all unchanged (preserves object identity for
	// assistant-ui's per-message converter memo).
	const out: DisplayMessage[] = [];
	for (let i = 0; i < messages.length; i++) {
		const message = messages[i];
		const raw = message as unknown as Record<string, unknown>;
		if (raw?.role === "toolResult") continue; // folded above

		const displayId = String(raw.id ?? `committed-${i}`);
		const running = displayId === runningId;
		const bag = resultsFor.get(message as object);
		const resultKey = bag
			? JSON.stringify(
					[...bag.entries()].map(([id, r]) => [id, r.isError, r.result]),
				)
			: "";

		const cached = cache?.get(message as object);
		if (cached && cached.running === running && cached.resultKey === resultKey) {
			out.push(cached.decorated);
			continue;
		}

		// (Re)build a decorated shallow clone. We clone the message + its content
		// (and the tool-call parts we attach results to) so source objects stay
		// untouched.
		const cloned: Record<string, unknown> = { ...raw };
		const content = raw.content;
		if (Array.isArray(content)) {
			cloned.content = content.map((p) => {
				if (!p || typeof p !== "object") return p;
				const copy = { ...(p as object) } as Record<string, unknown>;
				if (copy.type === "toolCall" && bag) {
					const r = bag.get(String(copy.id ?? ""));
					if (r) {
						copy.result = r.result;
						if (r.isError) copy.isError = true;
					}
				}
				return copy;
			});
		}
		const decorated = cloned as unknown as DisplayMessage;
		decorated.__displayId = displayId;
		decorated.__running = running;
		out.push(decorated);
		cache?.set(message as object, { decorated, running, resultKey });
	}

	return out;
}

/**
 * Map one (enriched) pi AgentMessage → assistant-ui ThreadMessageLike.
 * Stable module-level fn for useExternalMessageConverter's per-message memo.
 * Defensive: must never throw. Unknown roles/parts coerce to assistant text.
 */
export function convertMessage(message: AgentMessage): ThreadMessageLike {
	const m = message as unknown as Record<string, unknown>;
	const rawRole = m?.role;

	const role: "assistant" | "user" | "system" =
		rawRole === "user" ? "user" : rawRole === "system" ? "system" : "assistant";

	// Display decorations from toDisplayMessages (absent in direct unit calls).
	const id = typeof m.__displayId === "string" ? m.__displayId : undefined;
	// assistant-ui's fromThreadMessageLike only accepts `status` on ASSISTANT
	// messages (it throws otherwise). The streaming partial is always an
	// assistant turn, so we only emit status for assistant messages.
	const status: ThreadMessageLike["status"] | undefined =
		role === "assistant"
			? m.__running
				? { type: "running" }
				: { type: "complete", reason: "stop" }
			: undefined;

	const content = m?.content;

	// String content — the common user-message + simple-assistant case.
	if (typeof content === "string") {
		return {
			role,
			id,
			...(status ? { status } : {}),
			content: content ? [{ type: "text", text: content }] : [],
		};
	}

	if (Array.isArray(content)) {
		const parts: ThreadMessagePart[] = [];
		for (const part of content) {
			const p = part as Record<string, unknown>;
			switch (p?.type) {
				case "text":
					parts.push({ type: "text", text: String(p.text ?? "") });
					break;
				case "thinking":
					if (p.thinking)
						parts.push({ type: "reasoning", text: String(p.thinking) });
					break;
				case "toolCall": {
					const toolPart: Record<string, unknown> = {
						type: "tool-call",
						toolCallId: String(p.id ?? ""),
						toolName: String(p.name ?? "tool"),
						// pi-agent-core args are arbitrary JSON; assistant-ui types this
						// as ReadonlyJSONObject. Compatible at runtime.
						args: (p.arguments ?? {}) as Record<string, unknown>,
					};
					// toDisplayMessages folds the matching toolResult onto the part.
					if (p.result !== undefined) toolPart.result = p.result;
					if (p.isError) toolPart.isError = true;
					parts.push(toolPart as unknown as ThreadMessagePart);
					break;
				}
				case "image":
					if (typeof p.data === "string") {
						parts.push({
							type: "image",
							image: p.mimeType
								? `data:${p.mimeType};base64,${p.data}`
								: String(p.data),
						});
					}
					break;
				default:
					// Unknown part — stringify so it isn't dropped.
					if (p != null) parts.push({ type: "text", text: JSON.stringify(p) });
					break;
			}
		}
		return { role, id, ...(status ? { status } : {}), content: parts };
	}

	// Last-resort fallback — never throw.
	return { role, id, ...(status ? { status } : {}), content: [] };
}
